import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { stallsApi } from '@/api/stalls';
import { ApiHttpError } from '@/api/client';
import type { Stall } from '@/api/types';
import { StallCard } from '@/components/StallCard';
import { buildPinIcon } from '@/components/MapPin';
import { Skeleton } from '@/components/Skeleton';
import { getTimeOfDayBucket } from '@/hooks/useTimeOfDay';
import { haversineMeters } from '@/hooks/useGeofence';

// Bangkok centroid. The map opens here on first load; in v2 we'll geolocate.
const BKK_CENTER: [number, number] = [13.747, 100.523];

// Dev-only sample stalls. Used when the API is unreachable so designers can
// click through the home + stall flow without a running backend. NEVER
// rendered in production builds — see `usingMock` derivation below.
const MOCK_STALLS: Stall[] = [
  {
    id: 'mock-1',
    name_en: 'Jay Fai',
    name_th: 'เจ๊ไฝ',
    category: 'wok',
    dishes_en: ['Crab omelette', 'Drunken noodles'],
    dishes_th: ['ไข่เจียวปู', 'ผัดขี้เมา'],
    neighborhood_en: 'Banglamphu',
    neighborhood_th: 'บางลำพู',
    address_en: '327 Maha Chai Rd',
    address_th: '327 ถนนมหาไชย',
    lat: 13.7521,
    lng: 100.5043,
    status: 'open',
    fresh: true,
    just_opened: false,
    last_report_mins_ago: 4,
    open_votes_30m: 12,
    closed_votes_30m: 0,
    hours: '16:00–02:00',
    rating: 4.7,
    reviews: 218,
    photo_url: null,
  },
  {
    id: 'mock-2',
    name_en: 'Soi 38 Boat Noodle',
    name_th: 'ก๋วยเตี๋ยวเรือซอย 38',
    category: 'noodle',
    dishes_en: ['Boat noodle', 'Pork balls'],
    dishes_th: ['ก๋วยเตี๋ยวเรือ', 'ลูกชิ้นหมู'],
    neighborhood_en: 'Sukhumvit',
    neighborhood_th: 'สุขุมวิท',
    address_en: 'Soi Sukhumvit 38',
    address_th: 'ซอยสุขุมวิท 38',
    lat: 13.7274,
    lng: 100.5697,
    status: 'open',
    fresh: true,
    just_opened: true,
    last_report_mins_ago: 2,
    open_votes_30m: 7,
    closed_votes_30m: 1,
    hours: '10:00–22:00',
    rating: 4.5,
    reviews: 64,
    photo_url: null,
  },
  {
    id: 'mock-3',
    name_en: 'Yaowarat Toasted Bread',
    name_th: 'ขนมปังปิ้งเยาวราช',
    category: 'snack',
    dishes_en: ['Charcoal toast'],
    dishes_th: ['ขนมปังปิ้งเตาถ่าน'],
    neighborhood_en: 'Yaowarat',
    neighborhood_th: 'เยาวราช',
    address_en: 'Yaowarat Rd',
    address_th: 'ถ.เยาวราช',
    lat: 13.7396,
    lng: 100.5111,
    status: 'closed',
    fresh: false,
    just_opened: false,
    last_report_mins_ago: 22,
    open_votes_30m: 0,
    closed_votes_30m: 5,
    hours: '18:00–01:00',
    rating: 4.6,
    reviews: 41,
    photo_url: null,
  },
];

export function HomeRoute() {
  const { t } = useTranslation();
  const [center] = useState<[number, number]>(BKK_CENTER);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['stalls', 'nearby', center.join(',')],
    queryFn: ({ signal }) => stallsApi.nearby(center[0], center[1], 4000, signal),
    retry: (count, error) => {
      // Don't retry on 4xx errors.
      if (error instanceof ApiHttpError && error.status < 500) return false;
      return count < 2;
    },
    staleTime: 30_000,
  });

  const { stalls, usingMock } = useMemo(() => {
    if (data?.stalls && data.stalls.length > 0) {
      return { stalls: data.stalls, usingMock: false };
    }
    // Mock data only in dev. In production we surface an honest empty state.
    if (import.meta.env.DEV && (isError || (data && data.stalls?.length === 0))) {
      return { stalls: MOCK_STALLS, usingMock: true };
    }
    return { stalls: [] as Stall[], usingMock: false };
  }, [data, isError]);

  return (
    <div className="relative">
      <MapView center={center} stalls={stalls} />
      <BottomSheet stalls={stalls} loading={isLoading} usingMock={usingMock} center={center} />
      <Legend />
      <FilterChips />
    </div>
  );

  function FilterChips() {
    const chips: { key: string; label: string }[] = [
      { key: 'open_now', label: t('home.filter.open_now') },
      { key: 'noodle', label: t('home.filter.noodle') },
      { key: 'grill', label: t('home.filter.grill') },
      { key: 'isan', label: t('home.filter.isan') },
      { key: 'sweets', label: t('home.filter.sweets') },
      { key: 'late', label: t('home.filter.late') },
    ];
    return (
      <div
        // z-[1100] keeps the chips above Leaflet's tooltip pane (z=650)
        // and our own legend.
        className="absolute z-[1100] flex gap-2 overflow-x-auto px-3"
        style={{ top: 72, left: 0, right: 0, scrollbarWidth: 'none' }}
        role="tablist"
      >
        {chips.map((chip, i) => (
          <button
            key={chip.key}
            role="tab"
            aria-selected={i === 0}
            className={[
              'flex-none rounded-pill border px-3.5 py-2 text-[13px] font-semibold min-h-[36px]',
              i === 0
                ? 'border-soi-600 bg-soi-600 text-white'
                : 'border-[color:var(--border)] bg-white/90 text-ink-soft backdrop-blur',
            ].join(' ')}
          >
            {chip.label}
          </button>
        ))}
      </div>
    );
  }

  function Legend() {
    return (
      <div
        className="absolute right-3 z-[1100] rounded-pill border border-[color:var(--border)] bg-white/90 px-3 py-1.5 text-xs text-ink-soft shadow-s1 backdrop-blur"
        style={{ bottom: '70dvh' }}
      >
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ background: 'var(--open)' }}
        />{' '}
        {t('live.now')} ·{' '}
        <span
          className="inline-block h-2 w-2 rounded-full"
          style={{ background: 'var(--closed)' }}
        />{' '}
        {t('live.closed')}
      </div>
    );
  }
}

function MapView({ center, stalls }: { center: [number, number]; stalls: Stall[] }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.Marker[]>([]);
  const navigate = useNavigate();

  // Map lifecycle: init once, sync recenter as a separate effect.
  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const m = L.map(ref.current, { zoomControl: false, scrollWheelZoom: true }).setView(
      center,
      13,
    );
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OSM contributors',
    }).addTo(m);
    L.control.zoom({ position: 'bottomright' }).addTo(m);
    // Remove the map container from the default tab order; keyboard users
    // shouldn't land on the Leaflet div before the filter chips or the
    // bottom sheet. Arrow keys still pan/zoom when the map has focus.
    m.getContainer().setAttribute('tabindex', '-1');
    mapRef.current = m;
    return () => {
      m.remove();
      mapRef.current = null;
    };
    // Intentional: we init only once. Recenter happens in the next effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    m.setView(center, m.getZoom(), { animate: false });
  }, [center]);

  // Marker lifecycle: detach handlers + remove DOM before re-adding.
  useEffect(() => {
    const m = mapRef.current;
    if (!m) return;
    for (const mk of markersRef.current) {
      mk.off();
      mk.remove();
    }
    markersRef.current = [];
    markersRef.current = stalls.map((s) => {
      const mk = L.marker([s.lat, s.lng], { icon: buildPinIcon(s.status, s.fresh) }).addTo(m);
      mk.on('click', () => {
        navigate(`/stall/${encodeURIComponent(s.id)}`);
      });
      return mk;
    });
    return () => {
      for (const mk of markersRef.current) {
        mk.off();
        mk.remove();
      }
      markersRef.current = [];
    };
  }, [stalls, navigate]);

  return (
    <div
      ref={ref}
      role="region"
      aria-label="Bangkok stalls map"
      style={{ height: 'calc(100dvh - 60px)' }}
    />
  );
}

function BottomSheet({
  stalls,
  loading,
  usingMock,
  center,
}: {
  stalls: Stall[];
  loading: boolean;
  usingMock: boolean;
  center: [number, number];
}) {
  const { t } = useTranslation();
  const bucket = getTimeOfDayBucket();
  const empty = !loading && stalls.length === 0;

  return (
    <aside
      aria-label="Nearby stalls"
      // z-[1200] keeps the panel above Leaflet's popup pane (z=700) and the
      // filter chip strip (z=1100). Otherwise the entire panel paints under
      // the map tiles on desktop.
      className="fixed inset-x-0 bottom-0 z-[1200] max-h-[55dvh] overflow-y-auto rounded-t-2xl bg-[color:var(--surface)] p-4 md:absolute md:bottom-4 md:left-4 md:top-[120px] md:w-[380px] md:max-h-none md:rounded-2xl md:shadow-s3"
      style={{ boxShadow: '0 -8px 24px rgba(31, 31, 35, 0.10)' }}
    >
      <div className="mx-auto mb-3 h-1 w-11 rounded bg-[color:var(--border)]" aria-hidden="true" />
      <div className="mb-3 flex items-center justify-between">
        <h2 className="m-0 text-xl font-bold">
          {loading
            ? '…'
            : t('home.nearby_count', {
                count: stalls.length,
                defaultValue_one: '{{count}} stall',
                defaultValue_other: '{{count}} stalls',
              })}
        </h2>
        {usingMock && (
          <span className="rounded-pill bg-cream px-2 py-1 text-[11px] font-semibold uppercase text-ink-soft">
            {t('home.offline_preview')}
          </span>
        )}
      </div>
      {loading && (
        <div className="flex flex-col gap-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-md border border-[color:var(--border)] p-3">
              <div className="grid grid-cols-[56px_1fr] gap-3">
                <Skeleton width={56} height={56} rounded="md" />
                <div className="flex flex-col gap-1.5 pt-1">
                  <Skeleton width="60%" height={14} />
                  <Skeleton width="90%" height={12} />
                  <Skeleton width="40%" height={12} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {empty && (
        <div className="py-12 text-center">
          <div className="mb-2 text-4xl" aria-hidden="true">
            🥢
          </div>
          <h3 className="m-0 mb-1 text-xl font-bold">{t(`empty.${bucket}`)}</h3>
          <p className="m-0 text-sm text-ink-soft">{t('home.empty_hint')}</p>
        </div>
      )}
      {!loading && stalls.length > 0 && (
        <div className="flex flex-col gap-3">
          {stalls.map((s) => {
            const meters = haversineMeters(
              { lat: center[0], lng: center[1] },
              { lat: s.lat, lng: s.lng },
            );
            return <StallCard key={s.id} stall={s} distanceKm={meters / 1000} />;
          })}
        </div>
      )}
    </aside>
  );
}
